import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../config/supabase'
import './UserMenu.css'

function UserMenu({ onLogout }) {
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [profile, setProfile] = useState(null)
  const menuRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    if (!user) return

    const loadProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('avatar_url, first_name, last_name')
          .eq('id', user.id)
          .maybeSingle() // Usar maybeSingle() en lugar de single() para evitar error 406 si el perfil no existe

        if (error) {
          // Si es un error 406 o PGRST116 (no encontrado), simplemente no establecer el perfil
          if (error.code === 'PGRST116' || error.status === 406) {
            console.warn('Profile not found for user:', user.id)
            setProfile(null)
            return
          }
          throw error
        }
        
        setProfile(data)
      } catch (error) {
        console.error('Error loading profile for navbar:', error)
        // No establecer el perfil si hay error
        setProfile(null)
      }
    }

    loadProfile()
  }, [user])

  const getInitials = () => {
    // Priorizar nombres del perfil
    if (profile?.first_name && profile?.last_name) {
      const firstInitial = profile.first_name[0] || ''
      const lastInitial = profile.last_name[0] || ''
      return `${firstInitial}${lastInitial}`.toUpperCase()
    }
    // Si no hay perfil cargado aún, mostrar solo primera letra del email temporalmente
    if (user?.email) {
      // Solo mostrar una letra si el perfil aún no está cargado
      if (!profile) {
        return user.email[0].toUpperCase()
      }
      // Si el perfil está cargado pero no tiene nombres, usar email
      const emailParts = user.email.split('@')[0].split('.')
      if (emailParts.length >= 2) {
        return `${emailParts[0][0]}${emailParts[1][0]}`.toUpperCase()
      }
      return user.email[0].toUpperCase()
    }
    return 'U'
  }

  const handleDashboardClick = () => {
    navigate('/dashboard')
    setIsOpen(false)
  }

  const handleProfileClick = () => {
    navigate('/profile')
    setIsOpen(false)
  }

  const handleSignOut = () => {
    onLogout()
    setIsOpen(false)
  }

  return (
    <div className="user-menu-container" ref={menuRef}>
      <button 
        className="user-avatar-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
      >
        <div className="user-avatar">
          {profile?.avatar_url ? (
            <>
              <img 
                src={`${profile.avatar_url}?t=${Date.now()}`}
                alt={user?.email || 'User'}
                onError={(e) => {
                  // Si la imagen falla al cargar, mostrar las iniciales
                  e.target.style.display = 'none'
                  const initialElement = e.target.nextElementSibling
                  if (initialElement) initialElement.style.display = 'flex'
                }}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  borderRadius: '50%'
                }}
              />
              <span className="avatar-initial" style={{ display: 'none' }}>
                {getInitials()}
              </span>
            </>
          ) : (
            <span className="avatar-initial">
              {getInitials()}
            </span>
          )}
        </div>
        <svg 
          className={`arrow-icon ${isOpen ? 'open' : ''}`}
          width="12" 
          height="12" 
          viewBox="0 0 12 12" 
          fill="none"
        >
          <path 
            d="M6 9L1 4L11 4L6 9Z" 
            fill="currentColor"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <button className="menu-item" onClick={handleDashboardClick}>
            <svg className="menu-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="3" y="3" width="6" height="6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <rect x="11" y="3" width="6" height="6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <rect x="3" y="11" width="6" height="6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <rect x="11" y="11" width="6" height="6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
            <span>Dashboard</span>
          </button>
          
          <button className="menu-item" onClick={handleProfileClick}>
            <svg className="menu-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <path d="M3 18C3 14 6 12 10 12C14 12 17 14 17 18" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
            <span>Profile</span>
          </button>
          
          <div className="menu-divider"></div>
          
          <button className="menu-item sign-out" onClick={handleSignOut}>
            <svg className="menu-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7 2H4C2.89543 2 2 2.89543 2 4V16C2 17.1046 2.89543 18 4 18H7" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M13 14L17 10L13 6" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M17 10H7" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  )
}

export default UserMenu
